import React, { useReducer, useState, useEffect } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { updateLoadError } from "../../redux-store/currentUserSlice";
import { useSelector, useDispatch } from "react-redux";
import { Login, GoogleSignup } from "../../redux-store/currentUserActions";
import { GoogleLogin } from "@react-oauth/google";
import "../../styles/App.scss"
import "./index.css";
import { Typography, Box, Button, Container, Grid, Card, InputLabel, TextField, FormControlLabel, Checkbox, CircularProgress } from '@mui/material';
import { useSnackbar } from "../../contexts/CustomSnackbarContext";
import { FREE_EMAIL_DOMAINS } from "config/constants";
import { appConfig } from "redux-store/appConfigActions";
import { MixpanelService } from "utils/mixpanelService";

const LoginScreen = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const { openSnackbar } = useSnackbar();
  const [state, setState] = useReducer(reducer, {
    email: "",
    password: "",
    isLoading: false,
    errors: {},
    staySignedIn: false
  });
  const showEmailSentMessage = localStorage.getItem('showEmailSentMessage') === 'true';
  const [allowOnlyCompanyEmails, setAllowOnlyCompanyEmails] = useState(true);

  useEffect(() => {
    if (showEmailSentMessage) {
      const timer = setTimeout(() => {
        localStorage.removeItem('showEmailSentMessage');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [showEmailSentMessage]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectPath = urlParams.get('redirect');

    if (redirectPath) {
      setState({
        type: "SET_STATE",
        payload: { redirectPath: decodeURIComponent(redirectPath) },
      });
    }
  }, [location]);

  useEffect(() => {
    checkAppConfig();
  }, []);

  const checkAppConfig = async () => {
    try {
      const res = await dispatch(appConfig({})).unwrap();
      setAllowOnlyCompanyEmails(res.allow_only_company_emails);
    } catch (error) {
      console.error('Failed to fetch app config:', error);
    }
  }

  const responseMessage = async (response) => {
    try {
      setState({
        type: "SET_STATE",
        payload: { isLoading: true },
      });

      if (allowOnlyCompanyEmails) {
        const tokenData = JSON.parse(atob(response.credential.split('.')[1]));
        const emailDomain = tokenData.email.split('@')[1].toLowerCase().trim();
        const separatedEmailDomain = emailDomain.split('.');
        if (FREE_EMAIL_DOMAINS.includes(separatedEmailDomain[0])) {
          openSnackbar({
            message: "Please use your company email address to Sign Up.",
            severity: 'error'
          });
          return;
        }
      }

      const res = await dispatch(GoogleSignup({ credential: response.credential }));

      if (res.payload.message === 'Signed up successfully' || res.payload.message === 'Logged in successfully') {
        MixpanelService.track("login_success", { method: "google" });
        if (res.payload.has_organisation_or_project) history.push("/quick-start");
        else history.push("/home");
      } else {
        openSnackbar({
          message: res.payload.message || 'Login failed. Please try again.',
          severity: 'error'
        });
      }
    } catch (error) {
      openSnackbar({
        message: error.message || 'An error occurred during login. Please try again.',
        severity: 'error'
      });
    } finally {
      setState({
        type: "SET_STATE",
        payload: { isLoading: false },
      });
    }
  };

  const errorMessage = (error) => {
    dispatch(updateLoadError(error))
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setState({
      type: "SET_STATE",
      payload: { [name]: value },
    });
  };

  const handleCheckboxChange = (e) => {
    setState({
      type: "SET_STATE",
      payload: { staySignedIn: e.target.checked },
    });
  };

  const validateForm = () => {
    const errors = {};
    if (!state.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email)) {
      errors["email"] = "Please enter a valid email.";
    }
    if (!state.password) {
      errors["password"] = "Please enter Password.";
    }
    return errors;
  };

  const handleSubmit = async () => {
    MixpanelService.track("login_submitted", {});
    setState({
      type: "SET_STATE",
      payload: { isLoading: true },
    });

    const res = await dispatch(Login(state));

    setState({
      type: "SET_STATE",
      payload: { isLoading: false },
    });

    if (res.payload && res.payload.message === "Login successful") {
      MixpanelService.track("login_success", {});
      localStorage.setItem("showEmailSentMessage", 'false');

      if (state.redirectPath) {
        const pathToRedirect = state.redirectPath;

        setState({
          type: "SET_STATE",
          payload: { redirectPath: '' },
        });

        history.push(pathToRedirect);
      } else {
        if (res.payload.has_organisation_or_project) history.push("/quick-start");
        else history.push("/home");
      }
    } else {
      const errorMessage = res.payload ? res.payload : "Login failed.";
      openSnackbar({ message: errorMessage, severity: 'error' });
    }
  };

  const onFormSubmit = async () => {
    const errors = validateForm();
    setState({
      type: "SET_STATE",
      payload: { errors },
    });

    if (!Object.keys(errors).length) {
      await handleSubmit();
    }
  };

  let full_error = "";
  const errors = useSelector((state) => state.user.errors);

  if (errors) {
    Object.keys(errors).map((key) => (full_error = full_error + errors[key]));
  }

  return (
    <div className="bg-[var(--background-secondary)]">
      <Container maxWidth="md" className="full-height p-4">
        <Grid container spacing={2} className="d-flex justify-content-center align-items-center h-100">
          <Grid item xs={12} md={6} className="column-margin">
            {showEmailSentMessage && (
              <Box className="info-message">
                A confirmation email has been sent to your email address. Please confirm it to continue.
              </Box>
            )}
            <Card className="login-grid-card customBorder">
              <div className="right-column">
                <Box className="heading-container">
                  <Box className="create-account-title">
                    Sign In to Qodex
                  </Box>
                  <Box className="flex items-center justify-content-center">
                    <span>or</span>
                    <div
                      className="ml-2 cursor-pointer text-[var(--primary-color)]"
                      onClick={() => history.push('/')}
                    >
                      Create an Account
                    </div>
                  </Box>
                </Box>
                <Box>
                  <InputLabel htmlFor="email" className="input-label">Email</InputLabel>
                  <TextField
                    fullWidth
                    size="small"
                    className="input-field"
                    variant="outlined"
                    name="email"
                    value={state.email}
                    onChange={handleInputChange}
                    error={!!state.errors.email}
                    helperText={state.errors.email || ' '}
                  />

                  <InputLabel htmlFor="password" className="input-label">Password</InputLabel>
                  <TextField
                    fullWidth
                    size="small"
                    className="input-field"
                    variant="outlined"
                    type="password"
                    name="password"
                    value={state.password}
                    onChange={handleInputChange}
                    error={!!state.errors.password}
                    helperText={state.errors.password || ' '}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: -2, mb: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="staySignedIn"
                        color="primary"
                        checked={state.staySignedIn}
                        onChange={handleCheckboxChange}
                      />
                    }
                    label={
                      <Typography className='text-color'>
                        Stay signed in for 30 days
                      </Typography>
                    }
                  />
                  <Typography
                    component="span"
                    onClick={() => history.push('/forgot-password')}
                    className="forgot-password-link"
                    sx={{
                      color: 'var(--primary-color)',
                      textDecoration: 'none',
                      fontSize: '14px',
                      marginLeft: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Forgot Password?
                  </Typography>
                </Box>
                <div className="btn_login">
                  <Button
                    variant="contained"
                    onClick={onFormSubmit}
                    size="medium"
                    style={{ padding: '10px 0', width: '100%' }}
                    disabled={state.isLoading}
                  >
                    {state.isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </div>
                <Box className="mb-4 mt-4 center">
                  By signing in, I agree to the{" "}
                  <a href="https://www.qodex.ai/termsandconditions" className="terms" target="_blank" rel="noopener noreferrer">
                    Terms and Conditions
                  </a>.
                </Box>
                <Box className="line-container primary-color mb-4">
                  <span className="cutting-line"></span>
                  <Typography className="overlay-text">or</Typography>
                  <span className="cutting-line"></span>
                </Box>
                <div style={{ width: "100%" }}>
                  <GoogleLogin
                    buttonText="Sign in with Google"
                    onSuccess={responseMessage}
                    onFailure={errorMessage}
                    cookiePolicy={'single_host_origin'}
                    disabled={state.isLoading}
                  />
                </div>
                <div style={{ marginTop: "8px" }}></div>
              </div>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_STATE": {
      return {
        ...state,
        ...action.payload,
      };
    }
    default: {
      return state;
    }
  }
};

export default LoginScreen;